import {
  ACTIVE_ERROR_CASCADES,
  ACTIVE_ERROR_DESCRIPTION,
  ACTIVE_ERROR_TYPE,
  DashboardTileType,
  DeviceTypes,
  EventGroups,
  EventTypes,
  HeatUpSpeedType,
  RemoteDeviceType,
  RemoteServiceDiagnosisTag,
  RoomTypes,
  PowerLimitationControlCommandType,
  RoomsControlMode,
} from '@store';
import {
  AggregatedStatus,
  AnomalySeverity,
  DashboardAnomaliesNamesEnum,
  DeviceStatusType,
  DhwScheduleModeType,
  DiverterValve,
  GatewayConnectionMode,
  HeatingDeviceStatusType,
  HeatingFuelCellOperatingModesActive,
  HeatingSolarStatusType,
  PowerLimitationMethod,
  PowerLimitationType,
  StatusValueDcFeature,
  VentilationModes,
  VentilationModesBypassConfiguration,
  VentilationOperatingReasons,
  VentilationOperatingReasonsForTranslation,
} from '@vi-iot/client';

import { Injectable } from '@angular/core';
import { SCHEDULE_TYPE } from '../features/timemanager/schedule.enums';
import { Unit } from '../shared/common/units';
import { NO_VALUE } from '../shared/pipes';

export enum CustomerIdentifierType {
  User,
  System,
  Maintainer,
  Supporter,
  Admin,
}

@Injectable({ providedIn: 'root' })
export class CustomTranslationsService {
  DHW_OPERATING_MODES: Record<DhwOperatingMode, string> = {
    comfort: '+INSTALLATIONS.INSTALLATION.HEATING.DHW.COMFORT',
    eco: '+INSTALLATIONS.INSTALLATION.HEATING.DHW.ECO',
    balanced: '+INSTALLATIONS.INSTALLATION.HEATING.DHW.BALANCED',
    off: '+INSTALLATIONS.INSTALLATION.HEATING.DHW.OFF',
    efficientWithMinComfort: '+INSTALLATIONS.INSTALLATION.HEATING.DHW.EFFICIENT_WITH_MIN_COMFORT',
    efficient: '+INSTALLATIONS.INSTALLATION.HEATING.DHW.EFFICIENT',
  };

  GATEWAY_CONNECTION_MODE: Record<GatewayConnectionMode, string> = {
    ethernet: '+INSTALLATIONS.INSTALLATION.CONNECTION_MODE.LAN',
    wifi: '+INSTALLATIONS.INSTALLATION.CONNECTION_MODE.WIFI',
  };

  AQUASTAT_STATE: { [key: string]: string } = {
    active: '+INSTALLATIONS.INSTALLATION.WARM_WATER.AQUASTAT_STATE.ACTIVE',
    inactive: '+INSTALLATIONS.INSTALLATION.WARM_WATER.AQUASTAT_STATE.INACTIVE',
  };

  VENTILATION_MODES: Record<VentilationModes, string> = {
    standby: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_MODE.STANDBY',
    permanent: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_MODE.PERMANENT',
    ventilation: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_MODE.TIME_SCHEDULE',
    sensorOverride: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_MODE.SENSOR_OVERRIDE',
    sensorDriven: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_MODE.SENSOR_DRIVEN',
    off: '+INSTALLATIONS.INSTALLATION.VENTILATION.BYPASS_MODE.OFF',
    automaticSmoothRegulation: '+INSTALLATIONS.INSTALLATION.VENTILATION.BYPASS_MODE.SMOOTH',
    automaticDynamicRegulation: '+INSTALLATIONS.INSTALLATION.VENTILATION.BYPASS_MODE.DYNAMIC',
    standard: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_MODE.STANDARD',
    continuousSensorOverride:
      '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_MODE.CONTINUOUS_SENSOROVERRIDE',
    filterChange: '+INSTALLATIONS.INSTALLATION.VENTILATION.FILTER_CHANGE',
    closed: '+INSTALLATIONS.INSTALLATION.VENTILATION.BYPASS_MODE.CLOSED',
    open: '+INSTALLATIONS.INSTALLATION.VENTILATION.BYPASS_MODE.OPEN',
    automatic: '+INSTALLATIONS.INSTALLATION.VENTILATION.BYPASS_MODE.AUTOMATIC',
  };

  VENTILATION_MODES_BYPASS_CONFIGURATION: Record<VentilationModesBypassConfiguration, string> = {
    smooth: '+INSTALLATIONS.INSTALLATION.VENTILATION.BYPASS_CONFIGURATION.SMOOTH',
    dynamic: '+INSTALLATIONS.INSTALLATION.VENTILATION.BYPASS_CONFIGURATION.DYNAMIC',
  };

  HEATING_DEVICE_STATUS: Record<HeatingDeviceStatusType, string> = {
    [HeatingDeviceStatusType.ZeroBoilerAirDampers]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.ZERO_BOILER_AIR_DAMPERS',
    [HeatingDeviceStatusType.FlushBoilerCombChamber]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.FLUSH_BOILER_COMB_CHAMBER',
    [HeatingDeviceStatusType.FillBoilerDosingHopper]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.FILL_BOILER_DOSING_HOPPER',
    [HeatingDeviceStatusType.FillBoilerScrewConveyor]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.FILL_BOILER_SCREW_CONVEYOR',
    [HeatingDeviceStatusType.FillCombustionChamber]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.FILL_COMBUSTION_CHAMBER',
    [HeatingDeviceStatusType.IgniteBoiler]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.IGNITE_BOILER',
    [HeatingDeviceStatusType.IgniteBoilerFirebed]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.IGNITE_BOILER_FIREBED',
    [HeatingDeviceStatusType.BoilerIgniteRecharge]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BOILER_IGNITE_RECHARGE',
    [HeatingDeviceStatusType.BoilerLoadOperation]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BOILER_LOAD_OPERATION',
    [HeatingDeviceStatusType.BoilerRunOn]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BOILER_RUN_ON',
    [HeatingDeviceStatusType.BoilerStandby]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BOILER_STANDBY',
    [HeatingDeviceStatusType.RunScrewConveyourTillEmpty]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.RUN_SCREW_CONVEYOUR_TILL_EMPTY',
    [HeatingDeviceStatusType.FeedTubeWarm]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.FEED_TUBE_WARM',
    [HeatingDeviceStatusType.GrateCleaning]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.GRATE_CLEANING',
    [HeatingDeviceStatusType.DayHopperEmpty]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.DAY_HOPPER_EMPTY',
    [HeatingDeviceStatusType.CalibrateO2Probe]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.CALIBRATE_O2_PROBE',
    [HeatingDeviceStatusType.AshBoxFull]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.ASH_BOX_FULL',
    [HeatingDeviceStatusType.BoilerTestMode]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BOILER_TEST_MODE',
    [HeatingDeviceStatusType.BoilerResidHeatUtilisation]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BOILER_RESID_HEAT_UTILISATION',
    [HeatingDeviceStatusType.TransferHeat]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.TRANSFER_HEAT',
    [HeatingDeviceStatusType.Burner]: '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BURNER',
    [HeatingDeviceStatusType.CloseBoilerValves]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.CLOSE_BOILER_VALVES',
    [HeatingDeviceStatusType.BufferDrawing]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BUFFER_DRAWING',
    [HeatingDeviceStatusType.EnableAdditionalBoiler]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.ENABLE_ADDITIONAL_BOILER',
    [HeatingDeviceStatusType.BoilerHeatUp]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BOILER_HEAT_UP',
    [HeatingDeviceStatusType.BoilerOff]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BOILER_OFF',
    [HeatingDeviceStatusType.FillVacuumModule]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.FILL_VACUUM_MODULE',
    [HeatingDeviceStatusType.SolarBufferReheatingSuppression]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.SOLAR_BUFFER_REHEATING_SUPPRESSION',
    [HeatingDeviceStatusType.BoilerRechargingAllowed]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.BOILER_RECHARGING_ALLOWED',
    [HeatingDeviceStatusType.ChamberDoorOpen]:
      '+INSTALLATIONS.INSTALLATION.HEATING_DEVICE_STATUS.CHAMBER_DOOR_OPEN',
  };

  OPERATING_MODES: { [key: string]: string } = {
    dhwAndHeating: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.DWH_AND_HEATING',
    dhw: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.DHW',
    heating: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.HEATING',
    standby: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.STANDBY',
    external: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.EXTERNAL',
    forcedNormal: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.FORCED_NORMAL',
    forcedReduced: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.FORCED_REDUCED',
    normalStandby: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.NORMAL_STANDBY',
    dhwAndHeatingCooling:
      '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.DHW_HEATING_AND_COOLING',
    /* VIG-3066: dhwAndHeatingCooling is being replaced by dhwAndAuto */
    dhwAndAuto: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.DHW_HEATING_AND_COOLING',
    dhwAndCooling: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.DHW_AND_COOLING',
    heatingCooling: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.HEATING_AND_COOLING',
    cooling: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.COOLING',
    /* VIG-3066: Auto should use the same translation like heatingCooling */
    auto: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.HEATING_AND_COOLING',
    testMode: '+INSTALLATIONS.INSTALLATION.CIRCUIT.OPERATING_MODE.TEST_MODE',
  };

  STATE: Record<STATE, string> = {
    [STATE.on]: '+INSTALLATIONS.INSTALLATION.BOILER.OK',
    [STATE.off]: '+INSTALLATIONS.INSTALLATION.BOILER.NOK',
    [STATE.active]: '+INSTALLATIONS.INSTALLATION.ACTIVE_STATUS',
    [STATE.inactive]: '+INSTALLATIONS.INSTALLATION.INACTIVE_STATUS',
  };

  SCHEDULES: { [key: string]: string } = {
    'heating.circuits.heating.schedule': '+SCHEDULES.TAB.HEATING',
    'heating.circuits.dhw.schedule': '+SCHEDULES.TAB.WATER',
    'heating.circuits.circulation.schedule': '+SCHEDULES.TAB.PUMP',
    'heating.dhw.schedule': '+SCHEDULES.TAB.WATER',
    'heating.dhw.pumps.circulation.schedule': '+SCHEDULES.TAB.PUMP',
  };

  ESS_OPERATING_STATES: { [key: string]: string } = {
    off: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.OFF',
    initialization: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.INITIALIZATION',
    ready: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.READY',
    precharge: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.PRECHARGE',
    charge: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.CHARGE',
    discharge: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.DISCHARGE',
    safetyCheck: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.SAFETY_CHECK',
    error: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.ERROR',
    idle: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.IDLE',
    safetyCheckRequest: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.SAFETY_CHECK_REQUEST',
    shutDown: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.SHUT_DOWN',
    standby: '+INSTALLATIONS.INSTALLATION.ESS_OPERATING_STATE.STANDBY',
  };

  ESS_STATES_EXCEPTION: { [key: string]: string } = {
    batteryError: '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.BATTERY_ERROR',
    balancingFewDays: '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.BALANCING_FEW_DAYS',
    balancingFewHours: '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.BALANCING_FEW_HOURS',
    balancingOneWeeks: '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.BALANCING_ONE_WEEKS',
    balancingTwoWeeks: '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.BALANCING_TWO_WEEKS',
    batterySleepMode: '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.BATTERY_SLEEP_MODE',
    batteryCscPowerDown: '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.BATTERY_CSC_POWER_DOWN',
    islandModeBatteryLow:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.ISLAND_MODE_BATTERY_LOW',
    ambientTemperatureTooHigh:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.AMBIENT_TEMPERATURE_TOO_HIGH',
    ambientTemperatureTooLow:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.AMBIENT_TEMPERATURE_TOO_LOW',
    initialBatteryAdjustment:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.INITIAL_BATTERY_ADJUSTMENT',
    systemStateSelfTestActive:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.SYSTEM_STATE_SELF_TEST_ACTIVE',
    batteryDeepDischargeRecharge:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.BATTERY_DEEP_DISCHARGE_RECHARGE',
    systemStateIsloandModeActive:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.SYSTEM_STATE_ISLOAND_MODE_ACTIVE',
    systemStateUpdateInProgress:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.SYSTEMSTATE_UPDATE_IN_PROGRESS',
    batteryModuleExchangeAssistant:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.BATTERY_MODULE_EXCHANGE_ASSISTANT',
    batteryTemperatureDeratingActive:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.BATTERY_TEMPERATURE_DERATING_ACTIVE',
    islandModeRechargeToBackupStateOfCharge:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.RECHARGE.ISLAND_MODE_RECHARGE_TO_BACKUP_STATE_OF_CHARGE',
    systemStateIslandModeActive:
      '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.SYSTEM_STATE_ISLAND_MODE_ACTIVE',
    noException: '+INSTALLATIONS.INSTALLATION.ESS_STATES_EXCEPTION.NO_EXCEPTION',
  };

  ESS_STATES_BASIC: { [key: string]: string } = {
    initializing: '+INSTALLATIONS.INSTALLATION.ESS_STATES_BASIC.INITIALIZING',
    charge: '+INSTALLATIONS.INSTALLATION.ESS_STATES_BASIC.CHARGE',
    discharge: '+INSTALLATIONS.INSTALLATION.ESS_STATES_BASIC.DISCHARGE',
    lifetimeOptimization: '+INSTALLATIONS.INSTALLATION.ESS_STATES_BASIC.LIFETIME_OPTIMIZATION',
    maintenance: '+INSTALLATIONS.INSTALLATION.ESS_STATES_BASIC.MAINTENANCE',
    ready: '+INSTALLATIONS.INSTALLATION.ESS_STATES_BASIC.READY',
    systemStateStandby: '+INSTALLATIONS.INSTALLATION.ESS_STATES_BASIC.SYSTEM_STATE_STANDBY',
    systemStateError: '+INSTALLATIONS.INSTALLATION.ESS_STATES_BASIC.SYSTEM_STATE_ERROR',
  };

  ESS_PHOTOVOLTAIC_STATES_EXCEPTION: { [key: string]: string } = {
    photovoltaicError:
      '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_EXCEPTION.PHOTOVOLTAIC_ERROR',
    feedInLimitationActive:
      '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_EXCEPTION.FEED_IN_LIMITATION_ACTIVE',
    systemStateIslandModeActive:
      '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_EXCEPTION.SYSTEM_STATE_ISLAND_MODE_ACTIVE',
    systemStateSelfTestActive:
      '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_EXCEPTION.SYSTEM_STATE_SELF_TEST_ACTIVE',
    systemStateUpdateInProgress:
      '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_EXCEPTION.SYSTEM_STATE_UPDATE_IN_PROGRESS',
    limitEnergyManagementActive:
      '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_EXCEPTION.LIMIT_ENERGY_MANAGEMENT_ACTIVE',
    photovoltaicTemperatureDeratingPhotovoltaicActive:
      '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_EXCEPTION.PHOTOVOLTAIC_TEMPERATURE_DERATING_PHOTOVOLTAIC_ACTIVE',
  };

  ESS_PHOTOVOLTAIC_STATES_BASIC: { [key: string]: string } = {
    initializing: '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_BASIC.INITIALIZING',
    charge: '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_BASIC.CHARGE',
    discharge: '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_BASIC.DISCHARGE',
    lifetimeOptimization:
      '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_BASIC.LIFETIME_OPTIMIZATION',
    maintenance: '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_BASIC.MAINTENANCE',
    ready: '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_BASIC.READY',
    systemStateStandby:
      '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_BASIC.SYSTEM_STATE_STANDBY',
    systemStateError:
      '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_BASIC.SYSTEM_STATE_ERROR',
    producing: '+INSTALLATIONS.INSTALLATION.ESS_PHOTOVOLTAIC_STATES_BASIC.PRODUCING',
  };

  OPERATING_PROGRAMS: { [key: string]: string } = {
    unknown: NO_VALUE,
    manual: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.MANUAL',
    active: '+SCHEDULES.MODES.ACTIVE',
    comfort: '+SCHEDULES.MODES.COMFORT',
    eco: '+SCHEDULES.MODES.ECO',
    external: '+SCHEDULES.MODES.EXTERNAL',
    holiday: '+SCHEDULES.MODES.HOLIDAY',
    normal: '+SCHEDULES.MODES.NORMAL',
    reduced: '+SCHEDULES.MODES.REDUCED',
    notReduced: '+SCHEDULES.MODES.NOT_REDUCED',
    slightlyReduced: '+SCHEDULES.MODES.SLIGHTLY_REDUCED',
    maxReduced: '+SCHEDULES.MODES.MAX_REDUCED',
    standby: '+SCHEDULES.MODES.STANDBY',
    on: '+SCHEDULES.MODES.ON',
    off: '+SCHEDULES.MODES.OFF',
    home: '+SCHEDULES.MODES.HOME',
    away: '+SCHEDULES.MODES.AWAY',
    default: '+SCHEDULES.MODES.DEFAULT',
    antifreeze: '+SCHEDULES.MODES.ANTIFREEZE',
    frostprotection: '+SCHEDULES.MODES.ANTIFREEZE',
    night: '+SCHEDULES.MODES.NIGHT',
    ['temp-2']: '+SCHEDULES.MODES.TEMP_2',
    top: '+SCHEDULES.MODES.TOP',
    ['5/25-cycles']: '+SCHEDULES.MODES.5_25_CYCLES',
    ['5/10-cycles']: '+SCHEDULES.MODES.5_10_CYCLES',
    basic: '+SCHEDULES.MODES.BASIC',
    standard: '+SCHEDULES.MODES.STANDARD',
    intensive: '+SCHEDULES.MODES.INTENSIVE',
    fixed: '+SCHEDULES.MODES.FIXED',
    noDemandByZone: '+SCHEDULES.MODES.NO_DEMAND_BY_ZONE', // deprecated
    noDemand: '+SCHEDULES.MODES.NO_DEMAND_BY_ZONE',
    summerEco: '+SCHEDULES.MODES.SUMMER_ECO',
    summerSaving: '+SCHEDULES.MODES.SUMMER_SAVING',
    normalCoolingEnergySaving: '+SCHEDULES.MODES.NORMAL_COOLING_ENERGY_SAVING',
    reducedCoolingEnergySaving: '+SCHEDULES.MODES.REDUCED_COOLING_ENERGY_SAVING',
    comfortCoolingEnergySaving: '+SCHEDULES.MODES.COMFORT_COOLING_ENERGY_SAVING',
    manualTillNextSchedule: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.MANUAL',
    reducedCooling: '+SCHEDULES.MODES.REDUCED_COOLING',
    normalCooling: '+SCHEDULES.MODES.NORMAL_COOLING',
    comfortCooling: '+SCHEDULES.MODES.COMFORT_COOLING',
    manualCooling: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.MANUAL',
    manualTillNextScheduleCooling: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.MANUAL',
    reducedHeating: '+SCHEDULES.MODES.REDUCED',
    normalHeating: '+SCHEDULES.MODES.NORMAL_HEATING',
    comfortHeating: '+SCHEDULES.MODES.COMFORT',
    manualHeating: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.MANUAL',
    heatDissipation: '+SCHEDULES.MODES.HEAT_DISSIPATION',
    dhwPrecedence: '+SCHEDULES.MODES.DHW_PRECEDENCE',
    mixerSaving: '+SCHEDULES.MODES.MIXER_SAVING',
    dhwPrecendence: '+SCHEDULES.MODES.HEAT_DHWPRECENDENCE',
    screedDrying: '+SCHEDULES.MODES.SCREED_DRYING',
    manualTillNextScheduleHeating: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.MANUAL',
    normalEnergySaving: '+SCHEDULES.PROGRAMS.NORMAL_ENERGY_SAVING',
    comfortEnergySaving: '+SCHEDULES.PROGRAMS.COMFORT_ENERGY_SAVING',
    reducedEnergySaving: '+SCHEDULES.PROGRAMS.REDUCED_ENERGY_SAVING',
    manualEnergySaving: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.MANUAL',
    manualTillNextScheduleEnergySaving: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.MANUAL',
    levelOne: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.LEVEL_ONE',
    levelTwo: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.LEVEL_TWO',
    levelThree: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.LEVEL_THREE',
    levelFour: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.LEVEL_FOUR',
    timeScheduled: '+INSTALLATIONS.INSTALLATION.HEATING_CIRCUIT_OVERLAY.TIME_SCHEDULE.TITLE',
    cycledFix: '+INSTALLATIONS.INSTALLATION.WARM_WATER.DHW_CYCLED_FIX',
    externalDemand: '+INSTALLATIONS.INSTALLATION.WARM_WATER.DHW_EXTERNAL_DEMAND',
    cycledConfigured: '+INSTALLATIONS.INSTALLATION.WARM_WATER.DHW_CYCLE_CONFIGURED',
    permanent: '+INSTALLATIONS.INSTALLATION.WARM_WATER.DHW_PERMANENTLY',
    oneCycle: '1',
    twoCycles: '2',
    threeCycles: '3',
    fourCycles: '4',
    fiveCycles: '5',
    sixCycles: '6',
    low: '+SCHEDULES.MODES.LOW',
  };

  POWER_LIMITATION_HISTORY_LOG: { [key: string]: string } = {
    [PowerLimitationControlCommandType.ConsumptionLimitation]:
      '+INSTALLATIONS.INSTALLATIONS.POWER_LIMITATION_HISTORY_LOG.CONSUMPTION_LIMITATION',
    [PowerLimitationControlCommandType.ProductionLimitation]:
      '+INSTALLATIONS.INSTALLATIONS.POWER_LIMITATION_HISTORY_LOG.FEED_IN_LIMITATION',
  };

  // TODO apply : { [key in EventTypes | EventGroups]: string; } and fix missing events
  EVENT_LOG_EVENTS: { [key: string]: string } = {
    [EventTypes.DEVICE_ERROR]: '+EVENT_LOG.FILTERS.ERRORS',
    [EventTypes.DEVICE_MESSAGE_WARNING]: '+EVENT_LOG.FILTERS.DEVICE_MESSAGE_WARNING',
    [EventTypes.DEVICE_MESSAGE_SERVICE]: '+EVENT_LOG.FILTERS.DEVICE_MESSAGE_SERVICE',
    [EventTypes.DEVICE_MESSAGE_INFO]: '+EVENT_LOG.FILTERS.DEVICE_MESSAGE_INFO',
    [EventTypes.DEVICE_MESSAGE_STATUS]: '+EVENT_LOG.FILTERS.DEVICE_MESSAGE_STATUS',
    [EventTypes.HEATING_ROD_RUN_TOO_LONG]: '+EVENT_LOG.FILTERS.HEATING_ROD_RUN_TOO_LONG',
    [EventTypes.GATEWAY_ONLINE]: '+EVENT_LOG.FILTERS.GATEWAY_ONLINE',
    [EventTypes.FIRMWARE_UPDATE_STATUS]: '+EVENT_LOG.FILTERS.FIRMWARE_UPDATE_STATUS',
    [EventTypes.GATEWAY_RESET_COMMAND]: '+EVENT_LOG.FILTERS.GATEWAY_RESET_COMMAND',
    [EventTypes.FEATURE_CHANGED]: '+EVENT_LOG.FILTERS.FEATURE_CHANGED',
    [EventTypes.VIFLEX]: '+INSTALLATIONS.INSTALLATION.EVENT_LOG.NOTIFICATION.VIFLEX',
    [EventTypes.DYNAMIC_SAVING_TIPS_HEATINGCURVE_SAVINGS]:
      '+INSTALLATIONS.INSTALLATION.EVENT_LOG.NOTIFICATION.DYNAMIC_ENERGY_SAVING_TIPS',
    [EventTypes.SNAPSHOT_RESTORE_STATUS_CHANGED]:
      '+EVENT_LOG.FILTERS.SNAPSHOT_RESTORE_STATUS_CHANGED',
    [EventTypes.SRC_STATUS_CHANGED]: '+EVENT_LOG.FILTERS.SRC_STATUS_CHANGED',
    [EventTypes.GEOFENCING_ACTIVATION_CHANGED]: '+EVENT_LOG.FILTERS.GEOFENCING_ACTIVATION_CHANGED',
    [EventTypes.DEVICE_BMU_SERIAL_CHANGED]: '+EVENT_LOG.FILTERS.DEVICE_BMU_SERIAL_CHANGED',
    [EventTypes.DEVICE_BOILER_SERIAL_CHANGED]: '+EVENT_LOG.FILTERS.DEVICE_BOILER_SERIAL_CHANGED',
    [EventTypes.INSTALLATION_ADDRESS_CHANGED]: '+EVENT_LOG.FILTERS.INSTALLATION_ADDRESS_CHANGED',
    [EventTypes.INVITATION_ACCEPTED]: '+EVENT_LOG.FILTERS.INVITATION_ACCEPTED',
    [EventTypes.INVITATION_TERMINATED]: '+EVENT_LOG.FILTERS.INVITATION_TERMINATED',
    [EventTypes.EXTERNAL_LOCK]: '+EVENT_LOG.FILTERS.EXTERNAL_LOCK',
    [EventTypes.EXTERNAL_DEMAND]: '+EVENT_LOG.FILTERS.EXTERNAL_DEMAND',
    [EventGroups.SYSTEM_MESSAGES]: '+EVENT_LOG.FILTERS.GROUP.SYSTEM_MESSAGES',
    [EventGroups.COMMUNICATION_MODULE]: '+EVENT_LOG.FILTERS.GROUP.COMMUNICATION_MODULE',
    [EventGroups.SETTINGS]: '+EVENT_LOG.FILTERS.GROUP.SETTINGS',
    [EventGroups.INSTALLATION_DATA]: '+EVENT_LOG.FILTERS.GROUP.INSTALLATION_DATA',
    [EventGroups.INVITATIONS]: '+EVENT_LOG.FILTERS.GROUP.INVITATIONS',
  };

  MAINTENANCE_NOTIFICATIONS: Record<
    | ACTIVE_ERROR_TYPE.MAINTENANCE
    | ACTIVE_ERROR_TYPE.FILTER_EXCHANGE
    | ACTIVE_ERROR_TYPE.FILTER_EXCHANGE_14_DAYS
    | ACTIVE_ERROR_TYPE.SALT_CARTRIDGE_EXCHANGE
    | ACTIVE_ERROR_TYPE.MAINTENANCE_TIMEBASED
    | ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED
    | ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_30_D
    | ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_45_D
    | ACTIVE_ERROR_TYPE.DEVICE_BATTERY_LEVEL_LOW
    | ACTIVE_ERROR_TYPE.MAINTENANCE_BURNERBASED
    | ACTIVE_ERROR_TYPE.FILTER_CHANGE_MODE_ACTIVATED
    | ACTIVE_ERROR_DESCRIPTION,
    string
  > = {
    [ACTIVE_ERROR_TYPE.MAINTENANCE]: '+FEATURE_ENUM_STATUS.MAINTENANCE',
    [ACTIVE_ERROR_TYPE.FILTER_EXCHANGE]: '+ACTIVE_ERRORS.TYPE.FILTER_EXCHANGE',
    [ACTIVE_ERROR_TYPE.FILTER_EXCHANGE_14_DAYS]: '+ACTIVE_ERRORS.TYPE.FILTER_EXCHANGE_14_DAYS',
    [ACTIVE_ERROR_TYPE.FILTER_CHANGE_MODE_ACTIVATED]:
      '+ACTIVE_ERRORS.TYPE.FILTER_CHANGE_MODE_ACTIVATED',
    [ACTIVE_ERROR_DESCRIPTION.FILTER_EXCHANGE_14_DAYS]:
      '+INSTALLATIONS.INSTALLATION.ERROR_LIST.ACTIVE_ERROR_INFORMATION.FILTER_CHANGE',
    [ACTIVE_ERROR_TYPE.SALT_CARTRIDGE_EXCHANGE]: '+ACTIVE_ERRORS.TYPE.SALT_CARTRIDGE_EXCHANGE',
    [ACTIVE_ERROR_TYPE.MAINTENANCE_TIMEBASED]: '+ACTIVE_ERRORS.TYPE.MAINTENANCE_TIMEBASED',
    [ACTIVE_ERROR_TYPE.MAINTENANCE_BURNERBASED]: '+ACTIVE_ERRORS.TYPE.MAINTENANCE_BURNERBASED',
    [ACTIVE_ERROR_TYPE.DEVICE_BATTERY_LEVEL_LOW]: '+ACTIVE_ERRORS.TYPE.DEVICE_BATTERY_LEVEL_LOW',
    [ACTIVE_ERROR_DESCRIPTION.ROOM_NAME]:
      '+INSTALLATIONS.INSTALLATION.ERROR_LIST.ACTIVE_ERROR_INFORMATION.ROOM_NAME',
    [ACTIVE_ERROR_DESCRIPTION.ID]: '+INSTALLATIONS.INSTALLATION.ERROR_LIST.DEVICE_ID',
    [ACTIVE_ERROR_DESCRIPTION.DESCRIPTION]: '+INSTALLATIONS.INSTALLATION.ERROR_LIST.DEVICE_DESC',
    [ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED]: '+ACTIVE_ERRORS.TYPE.MAINTENANCE_NEEDED',
    [ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_30_D]:
      '+ACTIVE_ERRORS.TYPE.MAINTENANCE_NEEDED_IN_30_D',
    [ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_45_D]:
      '+ACTIVE_ERRORS.TYPE.MAINTENANCE_NEEDED_IN_45_D',
  };

  MAINTENANCE_NOTIFICATIONS_CASCADES: Record<ACTIVE_ERROR_CASCADES, string> = {
    [ACTIVE_ERROR_CASCADES.TCU]: '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.TCU',
    [ACTIVE_ERROR_CASCADES.ENERGY_MANAGEMENT]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.HEMS',
    [ACTIVE_ERROR_CASCADES.EEBUS]: '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.EEBUS',
    [ACTIVE_ERROR_CASCADES.UNKNOWN]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.UNKNOWN',
    [ACTIVE_ERROR_CASCADES.HEATING_DEVICES]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.HEATING',
    [ACTIVE_ERROR_CASCADES.VIRTUAL]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.VIRTUAL',
    [ACTIVE_ERROR_CASCADES.LOW_POWER_FUNK]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.ZIGBEE',
    [ACTIVE_ERROR_CASCADES.VENTILATION_DEVICE]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.VENTILATION',
    [ACTIVE_ERROR_CASCADES.ROOM_CONTROL]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.ROOMCONTROL',
    [ACTIVE_ERROR_CASCADES.VITOCONNECT]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.VITOCONNECT',
    [ACTIVE_ERROR_CASCADES.PHOTOVOLTAIC]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.3RDPARTYPV',
    [ACTIVE_ERROR_CASCADES.ELECTRICITY_STORAGE_DEVICE]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.ELECTRICITYSTORAGE',
    [ACTIVE_ERROR_CASCADES.CHARGING_STATION]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.VEHICLECHARGINGSTATION',
  };

  CASCADE_DEVICE_TYPES: Record<DeviceTypes, string> = {
    [DeviceTypes.Tcu]: '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.TCU',
    [DeviceTypes.Hems]: '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.HEMS',
    [DeviceTypes.EEBus]: '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.EEBUS',
    [DeviceTypes.UnknownType]: '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.UNKNOWN',
    [DeviceTypes.Heating]: '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.HEATING',
    [DeviceTypes.Virtual]: '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.VIRTUAL',
    [DeviceTypes.Zigbee]: '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.ZIGBEE',
    [DeviceTypes.Ventilation]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.VENTILATION',
    [DeviceTypes.RoomControl]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.ROOMCONTROL',
    [DeviceTypes.Vitoconnect]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.VITOCONNECT',
    [DeviceTypes.ThirdPartyPV]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.3RDPARTYPV',
    [DeviceTypes.ElectricityStorage]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.ELECTRICITYSTORAGE',
    [DeviceTypes.AirQualitySensor]:
      '+INSTALLATIONS.INSTALLATION.CASCADES.MULTIVALENT.DEVICE.AIRQUALITYSENSOR',
  };

  EVENT_LOG_USERNAME: Record<CustomerIdentifierType, string> = {
    [CustomerIdentifierType.User]: '+EVENT_LOG.USER_DESCRIPTION.END_USER',
    [CustomerIdentifierType.System]: '+EVENT_LOG.USER_DESCRIPTION.SYSTEM',
    [CustomerIdentifierType.Maintainer]: '+EVENT_LOG.USER_DESCRIPTION.MAINTAINER',
    [CustomerIdentifierType.Supporter]: '+EVENT_LOG.USER_DESCRIPTION.VIESSMANN_SUPPORTER',
    [CustomerIdentifierType.Admin]: '+EVENT_LOG.USER_DESCRIPTION.ADMINISTRATOR',
  };

  HEATING_SOLAR_STATUS: Record<HeatingSolarStatusType, string> = {
    [HeatingSolarStatusType.Standby]: '+INSTALLATIONS.INSTALLATION.HEATING_SOLAR_STATUS.STANDBY',
    [HeatingSolarStatusType.Automatic]:
      '+INSTALLATIONS.INSTALLATION.HEATING_SOLAR_STATUS.AUTOMATIC',
    [HeatingSolarStatusType.CollectorTooCold]:
      '+INSTALLATIONS.INSTALLATION.HEATING_SOLAR_STATUS.COLLECTOR_TOO_COLD',
    [HeatingSolarStatusType.RiskOfOverheating]:
      '+INSTALLATIONS.INSTALLATION.HEATING_SOLAR_STATUS.RISK_OF_OVERHEATING',
    [HeatingSolarStatusType.DhwHeating]:
      '+INSTALLATIONS.INSTALLATION.HEATING_SOLAR_STATUS.DHW_HEATING',
    [HeatingSolarStatusType.BufferCharging]:
      '+INSTALLATIONS.INSTALLATION.HEATING_SOLAR_STATUS.BUFFER_CHARGING',
    [HeatingSolarStatusType.MaximalDhwTemperature]:
      '+INSTALLATIONS.INSTALLATION.HEATING_SOLAR_STATUS.MAXIMAL_DHW_TEMPERATURE',
    [HeatingSolarStatusType.Wait]: '+INSTALLATIONS.INSTALLATION.HEATING_SOLAR_STATUS.WAIT',
    [HeatingSolarStatusType.Flush]: '+INSTALLATIONS.INSTALLATION.HEATING_SOLAR_STATUS.FLUSH',
    [HeatingSolarStatusType.RiskOfBufferOverheating]:
      '+INSTALLATIONS.INSTALLATION.HEATING_SOLAR_STATUS.RISK_OF_BUFFER_OVERHEATING',
  };

  HEAT_UP_SPEED_TYPES: Record<HeatUpSpeedType, string> = {
    [HeatUpSpeedType.VerySlow]: '+INSTALLATIONS.INSTALLATION.SRC.HEAT_UP_SPEED.VERY_SLOW',
    [HeatUpSpeedType.Slow]: '+INSTALLATIONS.INSTALLATION.SRC.HEAT_UP_SPEED.SLOW',
    [HeatUpSpeedType.Moderate]: '+INSTALLATIONS.INSTALLATION.SRC.HEAT_UP_SPEED.MODERATE',
    [HeatUpSpeedType.Normal]: '+INSTALLATIONS.INSTALLATION.SRC.HEAT_UP_SPEED.NORMAL',
    [HeatUpSpeedType.Fast]: '+INSTALLATIONS.INSTALLATION.SRC.HEAT_UP_SPEED.FAST',
    [HeatUpSpeedType.VeryFast]: '+INSTALLATIONS.INSTALLATION.SRC.HEAT_UP_SPEED.VERY_FAST',
  };

  ROOM_TYPES: Record<RoomTypes, string> = {
    [RoomTypes.BATHROOM]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM.BATHROOM',
    [RoomTypes.BEDROOM]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM.BEDROOM',
    [RoomTypes.HALLWAY]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM.HALLWAY',
    [RoomTypes.KITCHEN]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM.KITCHEN',
    [RoomTypes.LIVINGROOM]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM.LIVINGROOM',
    [RoomTypes.NURSERY]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM.NURSERY',
    [RoomTypes.OFFICE]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM.OFFICE',
    [RoomTypes.OTHER]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM.OTHER',
    [RoomTypes.TOILET]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM.TOILET',
    [RoomTypes.INVALID]: '',
  };

  ROOMS_CONTROL_MODE: Record<RoomsControlMode, string> = {
    [RoomsControlMode.COOLING]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM_CONTROL_MODE.COOLING',
    [RoomsControlMode.HEATING]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM_CONTROL_MODE.HEATING',
    [RoomsControlMode.HEATING_COOLING]:
      '+INSTALLATIONS.INSTALLATION.SRC.ROOM_CONTROL_MODE.HEATING_COOLING',
    [RoomsControlMode.STANDBY]: '+INSTALLATIONS.INSTALLATION.SRC.ROOM_CONTROL_MODE.STANDBY',
  };

  // TODO apply
  // : Record<RemoteDeviceType, string> = {
  //  fix missing
  REMOTE_DEVICE_TYPES: { [key: string]: string } = {
    [RemoteDeviceType.REPEATER]: '+SRC.DEVICE.SMART_REPEATER_GENERIC',
    [RemoteDeviceType.TRV]: '+SRC.DEVICE.SMART_DEVICE_ETRV_GENERIC',
    [RemoteDeviceType.CS]: '+SRC.DEVICE.SMART_CS_GENERIC',
    [RemoteDeviceType.FHT]: '+SRC.DEVICE.SMART_ZIGBEE_FHT_MAIN',
    [RemoteDeviceType.FHT_CHANNEL]: '+SRC.DEVICE.SMART_ZIGBEE_FHT_CHANNEL',
    [RemoteDeviceType.AIR_QUALITY_SENSOR]: '+SRC.DEVICE.SMART_AIR_QUALITY_SENSOR',
    [RemoteDeviceType.VITOTROL_100E]: '+SRC.DEVICE.VITOTROL_100E',
    [RemoteDeviceType.VITOTROL_300E]: '+SRC.DEVICE.VITOTROL_300E',
  };

  DEVICE_STATUS: Record<DeviceStatusType, string> = {
    [DeviceStatusType.Online]: '+INSTALLATIONS.INSTALLATION.GATEWAY_STATUS.STATUS.ONLINE',
    [DeviceStatusType.Offline]: '+INSTALLATIONS.INSTALLATION.GATEWAY_STATUS.STATUS.OFFLINE',
  };

  WIFI_STRENGTH: Record<WIFI_STRENGTH, string> = {
    excellent: '+INSTALLATIONS.INSTALLATION.WIFI_STRENGTH.EXCELLENT',
    good: '+INSTALLATIONS.INSTALLATION.WIFI_STRENGTH.GOOD',
    medium: '+INSTALLATIONS.INSTALLATION.WIFI_STRENGTH.MEDIUM',
    low: '+INSTALLATIONS.INSTALLATION.WIFI_STRENGTH.LOW',
    off: '+INSTALLATIONS.INSTALLATION.WIFI_STRENGTH.OFF',
  };

  DHW_ECO_MODES_LEADING: { [key: string]: string } = {
    top: '+INSTALLATIONS.INSTALLATION.HEATING.DHW.TOP',
    mid: '+INSTALLATIONS.INSTALLATION.HEATING.DHW.MIDDLE',
  };

  BUFFER_TANK_OPERATING_MODES: { [key: string]: string } = {
    heating: '+INSTALLATIONS.INSTALLATION.BUFFER_TANK.OPERATING_MODE.HEATING',
    cooling: '+INSTALLATIONS.INSTALLATION.BUFFER_TANK.OPERATING_MODE.COOLING',
  };

  VENTILATION_PROGRAMS: { [key: string]: string } = {
    off: 'GENERAL.OFF',
    levelOne: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.LEVEL_ONE',
    levelTwo: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.LEVEL_TWO',
    levelThree: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.LEVEL_THREE',
    levelFour: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.LEVEL_FOUR',
    silent: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.SILENT',
    intensive: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.INTENSIVE',
    temporaryShutdown:
      '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.TEMPORARY_SHUTDOWN',
    forcedLevelFour: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.INTENSIVE',
  };

  FUEL_CELL_OPERATING_MODES: Record<HeatingFuelCellOperatingModesActive, string> = {
    ecological: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_MODE.ECOLOGICAL',
    economical: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_MODE.ECONOMICAL',
    heatControlled: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_MODE.HEAT_CONTROLLED',
    maintenance: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_MODE.MAINTENANCE',
    standby: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_MODE.STANDBY',
    energyControlled: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_MODE.ENERGY_CONTROLLED',
    chimneySweeperMode: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_MODE.CHIMNEY_SWEEPER_MODE',
    party: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_MODE.PARTY',
    holidayWithFuelCellOn:
      '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_MODE.HOLIDAY_WITH_FUEL_CELL_ON',
    holidayWithFuelCellOff:
      '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_MODE.HOLIDAY_WITH_FUEL_CELL_OFF',
    unknown: NO_VALUE,
  };

  MOTOR_POSITION: { [key: string]: string } = {
    closed: '+INSTALLATIONS.INSTALLATION.ETRV.MOTOR_POSITION.FULLY_CLOSED',
    low: '+INSTALLATIONS.INSTALLATION.ETRV.MOTOR_POSITION.LOW',
    medium: '+INSTALLATIONS.INSTALLATION.ETRV.MOTOR_POSITION.MEDIUM',
    high: '+INSTALLATIONS.INSTALLATION.ETRV.MOTOR_POSITION.HIGH',
    open: '+INSTALLATIONS.INSTALLATION.ETRV.MOTOR_POSITION.FULLY_OPEN',
  };

  FUEL_CELL_OPERATING_PHASES: { [key: string]: string } = {
    standby: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_PHASE.STANDBY',
    startup: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_PHASE.STARTUP',
    generation: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_PHASE.GENERATION',
    stop: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_PHASE.STOP',
    maintenance: '+INSTALLATIONS.INSTALLATION.FUEL_CELL.OPERATING_PHASE.MAINTENANCE',
  };

  PHOTOVOLTAIC_STATES: { [key: string]: string } = {
    nothing: '+INSTALLATIONS.INSTALLATION.PHOTOVOLTAIC_STATES.OFF',
    error: '+INSTALLATIONS.INSTALLATION.PHOTOVOLTAIC_STATES.ERROR',
    ready: '+INSTALLATIONS.INSTALLATION.PHOTOVOLTAIC_STATES.STANDBY',
    production: '+INSTALLATIONS.INSTALLATION.PHOTOVOLTAIC_STATES.PRODUCTION',
  };

  PV_IMMERSION_STATUS: { [key: string]: string } = {
    standby: '+INSTALLATIONS.INSTALLATION.PV_HEATER.STANDBY',
    active: '+INSTALLATIONS.INSTALLATION.PV_HEATER.CONSUMING',
    error: '+INSTALLATIONS.INSTALLATION.PV_HEATER.ERROR',
  };

  ESS_SYSTEM_TYPES: { [key: string]: string } = {
    nothing: '-',
    hybrid: '+INSTALLATIONS.INSTALLATION.ESS_SYSTEM_TYPE.HYBRID',
    photovoltaic: '+INSTALLATIONS.INSTALLATION.ESS_SYSTEM_TYPE.PHOTOVOLTAIC',
    battery: '+INSTALLATIONS.INSTALLATION.ESS_SYSTEM_TYPE.BATTERY',
  };

  OPERATING_STATE_REASON: { [key: string]: string } = {
    unknown: NO_VALUE,
    manualTillNextSchedule: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.MANUAL',
    manual: '+INSTALLATIONS.INSTALLATION.CIRCUIT.HEATING_MODE.MANUAL',
    standby: '+SCHEDULES.MODES.STANDBY',
    heatOnTime: '+SRC.OPERATING_STATE.REASON.HEAT_ON_TIME',
    openWindow: '+SRC.OPERATING_STATE.REASON.OPEN_WINDOW',
    geoFencing: '+SRC.OPERATING_STATE.REASON.GEO_FENCING',
    holidayMode: '+SRC.OPERATING_STATE.REASON.HOLIDAY_MODE',
    holidayAtHome: '+SRC.OPERATING_STATE.REASON.HOLIDAY_AT_HOME',
    frostProtection: '+SRC.OPERATING_STATE.REASON.FROST_PROTECTION',
  };

  OPERATING_STATE_LEVEL: { [key: string]: string } = {
    unknown: NO_VALUE,
    reduced: '+SRC.OPERATING_STATE.REASON.REDUCED',
    normal: '+SRC.OPERATING_STATE.REASON.NORMAL',
    comfort: '+SRC.OPERATING_STATE.REASON.COMFORT',
  };

  OPERATING_STATE_DEMAND: { [key: string]: string } = {
    unknown: NO_VALUE,
    heating: '+SRC.OPERATING_STATE.DEMAND.HEATING',
    cooling: '+SRC.OPERATING_STATE.DEMAND.COOLING',
    energySaving: '+SRC.OPERATING_STATE.DEMAND.ENERGY_SAVING',
  };

  OPERATING_STATE_MODIFIER: { [key: string]: string } = {
    unknown: NO_VALUE,
    heatOnTime: '+SRC.OPERATING_STATE.MODIFIER.HEAT_ON_TIME',
    openWindow: '+SRC.OPERATING_STATE.MODIFIER.OPEN_WINDOW',
    geoFencing: '+SRC.OPERATING_STATE.MODIFIER.GEO_FENCING',
    frostProtection: '+SRC.OPERATING_STATE.MODIFIER.FROST_PROTECTION',
    none: '+SRC.OPERATING_STATE.MODIFIER.NONE',
  };

  HEATING_CURVE_PRESETS: { [key: string]: string } = {
    normalCentralHeating: '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.PRESET.NORMAL_CENTRAL_HEATING',
    underfloorCentralHeating:
      '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.PRESET.UNDER_FLOOR_CENTRAL_HEATING',
    swimmingPool: '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.PRESET.SWIMMING_POOL',
    baseboard: '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.PRESET.BASEBOARD',
    radiator: '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.PRESET.RADIATOR',
    radiantUnderfloorHeating:
      '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.PRESET.RADIAN_UNDERFLOOR_HEATING',
    snowmelt: '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.PRESET.SNOWMELT',
    airHandler: '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.PRESET.AIR_HANDLER',
    fanCoil: '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.PRESET.FAN_COIL',
  };

  HEATING_CONFIGURATION_REGULATION: { [key: string]: string } = {
    nothing: '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.CONFIGURATION_REGULATION.NOTHING',
    constantControlled:
      '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.CONFIGURATION_REGULATION.CONSTANT_CONTROLLED',
    weatherByOutsideSensorControlled:
      '+INSTALLATIONS.INSTALLATION.HEATING_ZONE.CONFIGURATION_REGULATION.WEATHER_BY_OUTSIDE_SENSOR_CONTROLLED',
  };

  AGGREGATED_STATUS: Record<AggregatedStatus, string> = {
    [AggregatedStatus.Error]: '+INSTALLATIONS.INSTALLATION.AGGREGATED_STATUS.FAULT',
    [AggregatedStatus.Offline]: '+INSTALLATIONS.INSTALLATION.AGGREGATED_STATUS.OFFLINE',
    [AggregatedStatus.Maintenance]: '+INSTALLATIONS.INSTALLATION.AGGREGATED_STATUS.MAINTENANCE',
    [AggregatedStatus.WorksProperly]:
      '+INSTALLATIONS.INSTALLATION.AGGREGATED_STATUS.WORKS_PROPERLY',
    [AggregatedStatus.RemoteDiagnosticSession]:
      '+INSTALLATIONS.INSTALLATIONS.DASHBOARD.REMOTE_DIAGNOSTIC',
    [AggregatedStatus.NBIoT]: '+INSTALLATIONS.INSTALLATION.AGGREGATED_STATUS.NBIOT',
    [AggregatedStatus.Locked]: '+INSTALLATIONS.INSTALLATION.AGGREGATED_STATUS.LOCKED',
  };

  INSTALLATION_STATUS_COLD_SYSTEM = '+INSTALLATIONS.INSTALLATION.COLD_SYSTEM';

  DASHBOARD_TILE_TYPE_TRANSLATIONS: Record<DashboardTileType, string> = {
    [DashboardTileType.InstallationsNotRegistered]:
      '+DASHBOARD.TITLES.INSTALLATIONS_NOT_REGISTERED',
    [DashboardTileType.InstallationsWithFault]: '+DASHBOARD.TITLES.INSTALLATIONS_WITH_FAULT',
    [DashboardTileType.ContractsEndingSoon]: '+DASHBOARD.TITLES.CONTRACTS_ENDING_SOON',
    [DashboardTileType.DevicesWithLowWifi]: '+DASHBOARD.TITLES.DEVICES_WITH_LOW_WIFI',
    [DashboardTileType.LastConnectedSystems]: '+DASHBOARD.TITLES.LAST_CONNECTED_SYSTEMS',
    [DashboardTileType.UpcomingMaintenanceForInstallations]:
      '+DASHBOARD.TITLES.UPCOMING_MAINTENANCE_FOR_INSTALLATIONS',
    [DashboardTileType.InstallationsWithAnomalies]:
      '+DASHBOARD.TITLES.INSTALLATIONS_WITH_ANOMALIES',
    [DashboardTileType.InstallationsWithoutHEMS]: '+DASHBOARD.TITLES.INSTALLATIONS_WITHOUT_HEMS',
    [DashboardTileType.InstallationsWithoutSRC]: '+DASHBOARD.TITLES.INSTALLATIONS_WITHOUT_SRC',
  };

  ON_OFF_STATES: { [key: string]: string } = {
    on: 'SHARED.STATE.ON',
    off: 'SHARED.STATE.OFF',
  };

  YES_NO_STATES: { [key: string]: string } = {
    yes: 'SHARED.STATE.YES',
    no: 'SHARED.STATE.NO',
  };

  DATE_PARTS: { [key: string]: string } = {
    day: 'GENERAL.TIME_DATE.DAY_REMAINING',
    days: 'GENERAL.TIME_DATE.DAYS_REMAINING',
    month: 'GENERAL.TIME_DATE.MONTH_REMAINING',
    months: 'GENERAL.TIME_DATE.MONTHS_REMAINING',
    year: 'GENERAL.TIME_DATE.YEAR_REMAINING',
    years: 'GENERAL.TIME_DATE.YEARS_REMAINING',
  };

  OPTIMIZATION_RECOMMENDATIONS = {
    heatingcurve: '+INSTALLATIONS.INSTALLATION.OPTIMIZATION_RECOMMENDATIONS.HEATING_CURVE',
    temperature: '+INSTALLATIONS.INSTALLATION.OPTIMIZATION_RECOMMENDATIONS.TEMPERATURE',
  };

  DHW_SCHEDULE_MODES: Record<DhwScheduleModeType, string> = {
    [DhwScheduleModeType.Automatic]: '+INSTALLATIONS.INSTALLATION.HEATING.DHW.AUTOMATIC',
    [DhwScheduleModeType.Individual]: '+INSTALLATIONS.INSTALLATION.HEATING.DHW.INDIVIDUAL',
  };

  HYDRAULIC_SCHEMATIC_TABLE_HEADERS: { [key: string]: string } = {
    id: '+INSTALLATIONS.INSTALLATION.HYDRAULIC_SCHEMATIC.TABLE.SVG_HEADER',
    feature: '+INSTALLATIONS.INSTALLATION.HYDRAULIC_SCHEMATIC.TABLE.VDD_DESCRIPTION_HEADER',
    value: '+INSTALLATIONS.INSTALLATION.HYDRAULIC_SCHEMATIC.TABLE.VDD_VALUE_HEADER',
  };

  ANOMALY_SEVERITY: { [key: string]: string } = {
    [AnomalySeverity.Critical]:
      '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.ANOMALY.CRITICAL',
    [AnomalySeverity.Warning]:
      '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.ANOMALY.WARNING',
    [AnomalySeverity.Optimization]:
      '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.ANOMALY.OPTIMIZATION',
    [AnomalySeverity.WorkingFine]:
      '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.ANOMALY.WORKING_FINE',
    [AnomalySeverity.Information]:
      '+DASHBOARD.DASHBOARD_TILES.INSTALLATIONS_WITH_ANOMALIES.ANOMALY.INFORMATION',
  };

  VENTILATION_REASONS_FOR_TRANSLATION: Record<VentilationOperatingReasonsForTranslation, string> = {
    silent: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.SILENT',
    forcedLevelFour:
      '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.INTENSIVE_VENTILATION',
    holiday: '+INSTALLATIONS.INSTALLATION.MENU_BAR.HOLIDAY_MODE.TITLE',
    eco: '+SCHEDULES.MODES.ECO',
    standby: '+SCHEDULES.MODES.STANDBY',
    comfort: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.COMFORT',
    filterChange: '+INSTALLATIONS.INSTALLATION.VENTILATION.FILTER_CHANGE',
  };

  REMOTE_SERVICE_DIAGNOSIS_TAGS: Record<RemoteServiceDiagnosisTag, string> = {
    beta: '+REMOTE_SERVICE.TAG_BETA',
    goldError: '+REMOTE_SERVICE.TAG_GOLD_ERROR',
    internalRsa: '+REMOTE_SERVICE.TAG_RSA_INTERNAL',
  };

  VENTILATION_REASONS: Record<VentilationOperatingReasons, string> = {
    silent: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.SILENT',
    forcedLevelFour:
      '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.INTENSIVE_VENTILATION',
    holiday: '+INSTALLATIONS.INSTALLATION.MENU_BAR.HOLIDAY_MODE.TITLE',
    eco: '+SCHEDULES.MODES.ECO',
    standby: '+SCHEDULES.MODES.STANDBY',
    comfort: '+INSTALLATIONS.INSTALLATION.VENTILATION.OPERATING_PROGRAM.COMFORT',
    filterChange: '+INSTALLATIONS.INSTALLATION.VENTILATION.FILTER_CHANGE',
    sensorOverride: NO_VALUE,
    sensorDriven: NO_VALUE,
    permanent: NO_VALUE,
    standard: NO_VALUE,
    schedule: NO_VALUE,
  };

  UNIT_TRANSLATION: { [key in Unit]: string } = {
    [Unit.Percent]: '+UNIT.PERCENT',
    [Unit.ShortPercent]: '+UNIT.PERCENT',
    [Unit.LiterHour]: '+UNIT.LITER_HOUR',
    [Unit.AmpereHour]: '+UNIT.AMPERE_HOUR',
    [Unit.GaloonMinute]: '+UNIT.GALOON_MINUTE',
    [Unit.CubicFeetPerHour]: '+UNIT.CUBIC_FEET_PER_HOUR',
    [Unit.Celsius]: '+UNIT.CELSIUS',
    [Unit.Fahrenheit]: '+UNIT.FAHRENHEIT',
    [Unit.Bar]: '+UNIT.BAR',
    [Unit.PoundPerSquareInch]: '+UNIT.PSI',
    [Unit.Ampere]: '+UNIT.AMPERE',
    [Unit.CubicMeter]: '+UNIT.CUBIC_METER',
    [Unit.CubicMeterPerHour]: '+UNIT.CUBIC_METER_PER_HOUR',
    [Unit.CubicFeet]: '+UNIT.CUBIC_FEET',
    [Unit.Dbm]: '+UNIT.DBM',
    [Unit.Decibar]: '+UNIT.DECIBAR',
    [Unit.Hour]: '+UNIT.HOUR',
    [Unit.Hours]: '+UNIT.HOURS',
    [Unit.Kilogram]: '+UNIT.KILOGRAM',
    [Unit.Ton]: '+UNIT.TON',
    [Unit.Kelvin]: '+UNIT.KELVIN',
    [Unit.KelvinPerMinute]: '+UNIT.KELVIN_PER_MINUTE',
    [Unit.Watt]: '+UNIT.WATT',
    [Unit.WattHours]: '+UNIT.WATT_HOURS',
    [Unit.WattHour]: '+UNIT.WATT_HOUR',
    [Unit.Kilowatt]: '+UNIT.KILOWATT',
    [Unit.KilowattHour]: '+UNIT.KILOWATT_HOUR',
    [Unit.Megawatt]: '+UNIT.MEGAWATT',
    [Unit.MegawattHour]: '+UNIT.MEGAWATT_HOUR',
    [Unit.Voltage]: '+UNIT.VOLTAGE',
    [Unit.KilowattPeak]: '+UNIT.KILOWATT_PEAK',
    [Unit.Liter]: '+UNIT.LITER',
    [Unit.LiterMinute]: '+UNIT.LITER_MINUTE',
    [Unit.Galoon]: '+UNIT.GALOON',
    [Unit.GaloonHour]: '+UNIT.GALOON_HOUR',
    [Unit.Minute]: '+UNIT.KILOMETRE',
    [Unit.MilliAmpere]: '+UNIT.MILLI_AMPERE',
    [Unit.Strength]: '+UNIT.STRENGTH',
    [Unit.PartsPerMillion]: '+UNIT.PARTS_PER_MILLION',
    [Unit.Kilometre]: '+UNIT.KILOMETRE',
    [Unit.PC]: '+UNIT.PC',
    [Unit.RotationPerMinute]: '+UNIT.ROTATION_PER_MINUTE',
    [Unit.Rps]: '+UNIT.RPS',
    [Unit.RevolutionsPerSecond]: '+UNIT.RPS',
    [Unit.NoUnit]: '+UNIT.NO_UNIT',
    // Artifical
    [Unit.DeciPoundPerSquareInch]: '+UNIT.WRONG_UNIT',
    [Unit.ShortWattHour]: '+UNIT.WRONG_UNIT',
    [Unit.ShortWatt]: '+UNIT.WATT',
    [Unit.ShortKilowatt]: '+UNIT.WRONG_UNIT',
    [Unit.ShortKilowattHour]: '+UNIT.WRONG_UNIT',
    [Unit.ShortMegawatt]: '+UNIT.WRONG_UNIT',
    [Unit.ShortMegawattHour]: '+UNIT.WRONG_UNIT',
    [Unit.ShortKilogram]: '+UNIT.WRONG_UNIT',
    [Unit.ShortTon]: '+UNIT.WRONG_UNIT',
    [Unit.Ohm]: '+UNIT.OHM',
    [Unit.kelvinMinute]: '+UNIT.KELVIN_MINUTE',
  };

  UNIT_TRANSLATION_LINE_CHART: { [key in Unit]: string } = {
    [Unit.Percent]: '+UNIT.PERCENT',
    [Unit.ShortPercent]: '+UNIT.PERCENT',
    [Unit.LiterHour]: '+UNIT.VOLUME_FLOW',
    [Unit.AmpereHour]: '+UNIT.AMPERE_HOUR',
    [Unit.GaloonMinute]: '+UNIT.VOLUME_FLOW',
    [Unit.CubicFeetPerHour]: '+UNIT.CUBIC_FEET_PER_HOUR',
    [Unit.Celsius]: '+UNIT.TEMPERATURE',
    [Unit.Fahrenheit]: '+UNIT.TEMPERATURE',
    [Unit.Bar]: '+UNIT.PRESSURE',
    [Unit.PoundPerSquareInch]: '+UNIT.PRESSURE',
    [Unit.Ampere]: '+UNIT.AMPERE',
    [Unit.CubicMeter]: '+UNIT.CUBIC_METER',
    [Unit.CubicMeterPerHour]: '+UNIT.CUBIC_METER_PER_HOUR',
    [Unit.CubicFeet]: '+UNIT.CUBIC_FEET',
    [Unit.Dbm]: '+UNIT.DBM',
    [Unit.Decibar]: '+UNIT.DECIBAR',
    [Unit.Hour]: '+UNIT.HOUR',
    [Unit.Hours]: '+UNIT.HOURS',
    [Unit.Kilogram]: '+UNIT.KILOGRAM',
    [Unit.Ton]: '+UNIT.TON',
    [Unit.Kelvin]: '+UNIT.KELVIN',
    [Unit.KelvinPerMinute]: '+UNIT.KELVIN_PER_MINUTE',
    [Unit.Watt]: '+UNIT.WATT',
    [Unit.WattHour]: '+UNIT.WATT_HOUR',
    [Unit.WattHours]: '+UNIT.WATT_HOURS',
    [Unit.Kilowatt]: '+UNIT.KILOWATT',
    [Unit.KilowattHour]: '+UNIT.KILOWATT_HOUR',
    [Unit.Megawatt]: '+UNIT.MEGAWATT',
    [Unit.MegawattHour]: '+UNIT.MEGAWATT_HOUR',
    [Unit.Voltage]: '+UNIT.VOLTAGE',
    [Unit.KilowattPeak]: '+UNIT.KILOWATT_PEAK',
    [Unit.Liter]: '+UNIT.LITER',
    [Unit.LiterMinute]: '+UNIT.LITER_MINUTE',
    [Unit.Galoon]: '+UNIT.GALOON',
    [Unit.GaloonHour]: '+UNIT.GALOON_HOUR',
    [Unit.Minute]: '+UNIT.KILOMETRE',
    [Unit.MilliAmpere]: '+UNIT.MILLI_AMPERE',
    [Unit.Strength]: '+UNIT.STRENGTH',
    [Unit.PartsPerMillion]: '+UNIT.PARTS_PER_MILLION',
    [Unit.Kilometre]: '+UNIT.KILOMETRE',
    [Unit.PC]: '+UNIT.PC',
    [Unit.RotationPerMinute]: '+UNIT.ROTATION_PER_MINUTE',
    [Unit.Rps]: '+UNIT.RPS',
    [Unit.RevolutionsPerSecond]: '+UNIT.RPS',
    [Unit.NoUnit]: '+UNIT.NO_UNIT',
    // Artifical
    [Unit.DeciPoundPerSquareInch]: '+UNIT.WRONG_UNIT',
    [Unit.ShortWattHour]: '+UNIT.WRONG_UNIT',
    [Unit.ShortWatt]: '+UNIT.WATT',
    [Unit.ShortKilowatt]: '+UNIT.WRONG_UNIT',
    [Unit.ShortKilowattHour]: '+UNIT.WRONG_UNIT',
    [Unit.ShortMegawatt]: '+UNIT.WRONG_UNIT',
    [Unit.ShortMegawattHour]: '+UNIT.WRONG_UNIT',
    [Unit.ShortKilogram]: '+UNIT.WRONG_UNIT',
    [Unit.ShortTon]: '+UNIT.WRONG_UNIT',
    [Unit.Ohm]: '+UNIT.OHM',
    [Unit.kelvinMinute]: '+UNIT.KELVIN_MINUTE',
  };

  VENTILATION_SERVICE_LAYER_STATUS: { [key: string]: string } = {
    [VentilationServiceLayerStatus.standby]:
      '+INSTALLATIONS.INSTALLATION.SERVICE_LAYER.STATUS.STAND_BY',
    [VentilationServiceLayerStatus.inPreparation]:
      '+INSTALLATIONS.INSTALLATION.SERVICE_LAYER.STATUS.IN_PREPARATION',
    [VentilationServiceLayerStatus.active]:
      '+INSTALLATIONS.INSTALLATION.SERVICE_LAYER.STATUS.ACTIVE',
    [VentilationServiceLayerStatus.postProcessing]:
      '+INSTALLATIONS.INSTALLATION.SERVICE_LAYER.STATUS.POST_PROCESSING',
  };

  ANOMALIES_NAMES: Record<DashboardAnomaliesNamesEnum, string> = {
    [DashboardAnomaliesNamesEnum.OnlineAvaibilityState]:
      '+REMOTE_SERVICE.ONLINE_AVAILABILITY_STATE',
    [DashboardAnomaliesNamesEnum.LeakageGradientAnomaly]:
      '+REMOTE_SERVICE.LEAKAGE_GRADIENT_ANOMALY',
    [DashboardAnomaliesNamesEnum.LeakageLossAnomaly]: '+REMOTE_SERVICE.LEAKAGE_LOSS_ANOMALY',
    [DashboardAnomaliesNamesEnum.LeakageThresholdAnomalyLower]:
      '+REMOTE_SERVICE.LEAKAGE_THRESHOLD_ANOMALY_LOWER',
    [DashboardAnomaliesNamesEnum.RemainingUsefulLifeOfFuelCell]:
      '+REMOTE_SERVICE.REMAINING_USEFUL_LIFE_OF_FUEL_CELL',
    [DashboardAnomaliesNamesEnum.OutputGradientAnomaly]: '+REMOTE_SERVICE.OUTPUT_GRADIENT_ANOMALY',
    [DashboardAnomaliesNamesEnum.OutputThresholdAnomaly]:
      '+REMOTE_SERVICE.OUTPUT_THRESHOLD_ANOMALY',
    [DashboardAnomaliesNamesEnum.DhwDurationAnomaly]: '+REMOTE_SERVICE.DHW_DURATION_ANOMALY',
    [DashboardAnomaliesNamesEnum.DhwEfficiencyAnomaly]: '+REMOTE_SERVICE.DHW_EFFICIENCY_ANOMALY',
    [DashboardAnomaliesNamesEnum.DhwStartsAnomaly]: '+REMOTE_SERVICE.DHW_STARTS_ANOMALY',
    [DashboardAnomaliesNamesEnum.BurnerAnomaly]: '+REMOTE_SERVICE.BURNER_ANOMALY',
    [DashboardAnomaliesNamesEnum.GasFanAnomaly]: '+REMOTE_SERVICE.GAS_FAN_ANOMALY',
    [DashboardAnomaliesNamesEnum.InternalPumpAnomaly]: '+REMOTE_SERVICE.INTERNAL_PUMP_ANOMALY',
    [DashboardAnomaliesNamesEnum.ThreeWayValveAnomaly]: '+REMOTE_SERVICE.3WAY_VALVE_ANOMALY',
    [DashboardAnomaliesNamesEnum.TooLowRuntimeCheck]: '+REMOTE_SERVICE.TOO_LOW_RUNTIME_CHECK',
    [DashboardAnomaliesNamesEnum.OutsideTemperatureSensor]:
      '+REMOTE_SERVICE.OUTSIDE_TEMPERATURE_SENSOR',
    [DashboardAnomaliesNamesEnum.FlueTemperatureHeating]:
      '+REMOTE_SERVICE.FLUE_TEMPERATURE_HEATING',
    [DashboardAnomaliesNamesEnum.FlueTemperatureDhw]: '+REMOTE_SERVICE.FLUE_TEMPERATURE_DHW',
    [DashboardAnomaliesNamesEnum.FuelCellRuntimeNoRootCauseFound]:
      '+REMOTE_SERVICE.FUEL_CELL_RUNTIME_NO_ROOT_CAUSE_FOUND',
    [DashboardAnomaliesNamesEnum.FuelCellRuntimeSensorsCheck]:
      '+REMOTE_SERVICE.FUEL_CELL_RUNTIME_SENSORS_CHECK',
    [DashboardAnomaliesNamesEnum.FuelCellRuntimeCirculationCheck]:
      '+REMOTE_SERVICE.FUEL_CELL_RUNTIME_CIRCULATION_CHECK',
    [DashboardAnomaliesNamesEnum.FuelCellRuntimeUsageCheck]:
      '+REMOTE_SERVICE.FUEL_CELL_RUNTIME_USAGE_CHECK',
    [DashboardAnomaliesNamesEnum.FuelCellRuntimeSetPointCheck]:
      '+REMOTE_SERVICE.FUEL_CELL_RUNTIME_SET_POINT_CHECK',
    [DashboardAnomaliesNamesEnum.BurnerClockingCheck]: '+REMOTE_SERVICE.BURNER_CLOCKING_CHECK',
    [DashboardAnomaliesNamesEnum.WaterRefill]: '+REMOTE_SERVICE.WATER_REFILL',
    [DashboardAnomaliesNamesEnum.OutletTemperaturePrediction]:
      '+REMOTE_SERVICE.OUTLET_TEMPERATURE_PREDICTION',
    [DashboardAnomaliesNamesEnum.SolarThermalCheck]: '+REMOTE_SERVICE.SOLAR_THERMAL_CHECK',
    [DashboardAnomaliesNamesEnum.SolarCollectorSensor]: '+REMOTE_SERVICE.SOLAR_COLLECTOR_SENSOR',
    [DashboardAnomaliesNamesEnum.SolarDhwSensor]: '+REMOTE_SERVICE.SOLAR_DHW_SENSOR',
    [DashboardAnomaliesNamesEnum.VolumetricFlowDhwCheck]:
      '+REMOTE_SERVICE.VOLUMETRIC_FLOW_DHW_CHECK',
    [DashboardAnomaliesNamesEnum.HeatingRodDhwDuration]: '+REMOTE_SERVICE.HEATING_ROD_DHW_DURATION',
    [DashboardAnomaliesNamesEnum.HeatingRodHeatingBivalence]:
      '+REMOTE_SERVICE.HEATING_ROD_HEATING_BIVALENCE',
    [DashboardAnomaliesNamesEnum.Algo03NightSwitchOns]: '+REMOTE_SERVICE.ALGO03_NIGHT_SWITCH_ONS',
    [DashboardAnomaliesNamesEnum.Algo04NightCirculation]:
      '+REMOTE_SERVICE.ALGO04_NIGHT_CIRCULATION',
    [DashboardAnomaliesNamesEnum.Algo05aDecreasingDhwPumpEvents]:
      '+REMOTE_SERVICE.ALGO05A_DECREASING_DHW_PUMP_EVENTS',
    [DashboardAnomaliesNamesEnum.Algo05bQ25DhwTempAtStop]:
      '+REMOTE_SERVICE.ALGO05B_Q25_DHW_TEMP_AT_STOP',
    [DashboardAnomaliesNamesEnum.Algo05cQ50DhwTempAtStop]:
      '+REMOTE_SERVICE.ALGO05C_Q50_DHW_TEMP_AT_STOP',
    [DashboardAnomaliesNamesEnum.Algo06Stagnation]: '+REMOTE_SERVICE.ALGO06_STAGNATION',
    [DashboardAnomaliesNamesEnum.Algo07HighDifferenceSolarPumpEvents]:
      '+REMOTE_SERVICE.ALGO07_HIGH_DIFFERENCE_SOLAR_PUMP_EVENTS',
    [DashboardAnomaliesNamesEnum.Algo08DecreasingCollectorStops]:
      '+REMOTE_SERVICE.ALGO08_DECREASING_COLLECTOR_STOPS',
    [DashboardAnomaliesNamesEnum.Algo09TemperatureArea]: '+REMOTE_SERVICE.ALGO09_TEMPERATURE_AREA',
    [DashboardAnomaliesNamesEnum.CompressorClockingCheck]:
      '+REMOTE_SERVICE.COMPRESSOR_CLOCKING_CHECK',
    [DashboardAnomaliesNamesEnum.IgnitionTimesCheck]: '+REMOTE_SERVICE.IGNITION_TIMES_CHECK',
    [DashboardAnomaliesNamesEnum.CompressorAnomaly]: '+REMOTE_SERVICE.COMPRESSOR_ANOMALY',
    [DashboardAnomaliesNamesEnum.ThreeWayValveHeatpumpAnomaly]:
      '+REMOTE_SERVICE.3WAY_VALVE_HEATPUMP_ANOMALY',
    [DashboardAnomaliesNamesEnum.HeatingHeatpumpCheck]: '+REMOTE_SERVICE.HEATING_HEATPUMP_CHECK',
    [DashboardAnomaliesNamesEnum.DhwHeatpumpCheck]: '+REMOTE_SERVICE.DHW_HEATPUMP_CHECK',
    [DashboardAnomaliesNamesEnum.CoolingHeatpumpCheck]: '+REMOTE_SERVICE.COOLING_HEATPUMP_CHECK',
    [DashboardAnomaliesNamesEnum.HeatingBurnerCheck]: '+REMOTE_SERVICE.HEATING_BURNER_CHECK',
    [DashboardAnomaliesNamesEnum.DhwBurnerCheck]: '+REMOTE_SERVICE.DHW_BURNER_CHECK',
    [DashboardAnomaliesNamesEnum.RefrigerantPressure]: '+REMOTE_SERVICE.REFRIGERANT_PRESSURE',
    [DashboardAnomaliesNamesEnum.RefrigerantLeakageShortTerm]:
      '+REMOTE_SERVICE.REFRIGERANT_LEAKAGE_SHORT_TERM',
    [DashboardAnomaliesNamesEnum.RefrigerantLeakageLongTerm]:
      '+REMOTE_SERVICE.REFRIGERANT_LEAKAGE_LONG_TERM',
    [DashboardAnomaliesNamesEnum.MembraneExpansionVesselCheck]:
      '+REMOTE_SERVICE.MEMBRANE_EXPANSION_VESSEL_CHECK',
    [DashboardAnomaliesNamesEnum.DhwChargingsCheck]: '+REMOTE_SERVICE.DHW_CHARGINGS_CHECK',
    [DashboardAnomaliesNamesEnum.HydrSeparatorSensorCheck]:
      '+REMOTE_SERVICE.HYDR_SEPARATOR_SENSOR_CHECK',
    [DashboardAnomaliesNamesEnum.HeatingHydrSepToMainCheck]:
      '+REMOTE_SERVICE.HEATING_HYDR_SEP_TO_MAIN_CHECK',
    [DashboardAnomaliesNamesEnum.BurnerClockingLongTermCheck]:
      '+REMOTE_SERVICE.BURNER_CLOCKING_LONG_TERM_CHECK',
    [DashboardAnomaliesNamesEnum.CompressorClockingLongTermCheck]:
      '+REMOTE_SERVICE.COMPRESSOR_CLOCKING_LONG_TERM_CHECK',
    [DashboardAnomaliesNamesEnum.RefrigerantLeakageShortTermDhw]:
      '+REMOTE_SERVICE.REFRIGERANT_LEAKAGE_SHORT_TERM_DHW',
    [DashboardAnomaliesNamesEnum.RefrigerantLeakageShortTermCooling]:
      '+REMOTE_SERVICE.REFRIGERANT_LEAKAGE_SHORT_TERM_COOLING',
    [DashboardAnomaliesNamesEnum.OutletTempSensor]: '+REMOTE_SERVICE.OUTLET_TEMP_SENSOR',
    [DashboardAnomaliesNamesEnum.FuelCellRuntimeErrorCheck]:
      '+REMOTE_SERVICE.FUEL_CELL_RUNTIME_ERROR_CHECK',
    [DashboardAnomaliesNamesEnum.ConnectionSwitchOffCheck]:
      '+REMOTE_SERVICE.CONNECTION_SWITCH_OFF_CHECK',
    [DashboardAnomaliesNamesEnum.MainHeatExchangerDhwConfigCheck]:
      '+REMOTE_SERVICE.MAIN_HEAT_EXHANGER_DHW_CONFIG_CHECK',
    [DashboardAnomaliesNamesEnum.VolFlowGradientAnomalyDhw]:
      '+REMOTE_SERVICE.VOL_FLOW_GRADIENT_ANOMALY_DHW',
    [DashboardAnomaliesNamesEnum.VolFlowLossAnomalyDhw]:
      '+REMOTE_SERVICE.VOL_FLOW_LOSS_ANOMALY_DHW',
    [DashboardAnomaliesNamesEnum.GasConsumptionCalculation]:
      '+REMOTE_SERVICE.GAS_CONSUMPTION_CALCULATION',
    [DashboardAnomaliesNamesEnum.AmbientTemperatureCheck]:
      '+REMOTE_SERVICE.AMBIENT_TEMPERATURE_CHECK',
    [DashboardAnomaliesNamesEnum.ForwardFilledGeneralActivityFeatures]:
      '+REMOTE_SERVICE.FORWARD_FILLED_GENERAL_ACTIVITY_FEATURES',
    [DashboardAnomaliesNamesEnum.VolFlowGradientAnomalyDefrost]:
      '+REMOTE_SERVICE.VOL_FLOW_GRADIENT_ANOMALY_DEFROST',
    [DashboardAnomaliesNamesEnum.VolFlowLossAnomalyDefrost]:
      '+REMOTE_SERVICE.VOL_FLOW_LOSS_ANOMALY_DEFROST',
    [DashboardAnomaliesNamesEnum.HeatingRodHeatingEmergencyModeCheck]:
      '+REMOTE_SERVICE.HEATING_ROD_HEATING_EMERGENCY_MODE_CHECK',
    [DashboardAnomaliesNamesEnum.HeatingRodHeatingScreedDryingCheck]:
      '+REMOTE_SERVICE.HEATING_ROD_HEATING_SCREED_DRYING_CHECK',
    [DashboardAnomaliesNamesEnum.HeatingRodHeatingPowerOffCheck]:
      '+REMOTE_SERVICE.HEATING_ROD_HEATING_POWER_OFF_CHECK',
    [DashboardAnomaliesNamesEnum.HeatingRodHeatingNoiseReductionCheck]:
      '+REMOTE_SERVICE.HEATING_ROD_HEATING_NOISE_REDUCTION_CHECK',
    [DashboardAnomaliesNamesEnum.HeatingRodHeatingDefrostCheck]:
      '+REMOTE_SERVICE.HEATING_ROD_HEATING_DEFROST_CHECK',
    [DashboardAnomaliesNamesEnum.DefrostPreparationEfficiency]:
      '+REMOTE_SERVICE.DEFROST_PREPARATION_EFFICIENCY',
    [DashboardAnomaliesNamesEnum.DefrostEfficiency]: '+REMOTE_SERVICE.DEFROST_EFFICIENCY',
    [DashboardAnomaliesNamesEnum.EvaporationEfficiency]: '+REMOTE_SERVICE.EVAPORATION_EFFICIENCY',
    [DashboardAnomaliesNamesEnum.HeatingRodHeatingModeOnErrorCheck]:
      '+REMOTE_SERVICE.HEATING_ROD_HEATING_MODE_ON_ERROR_CHECK',
    [DashboardAnomaliesNamesEnum.HeatingRodHeatingBlockingHpErrorCheck]:
      '+REMOTE_SERVICE.HEATING_ROD_HEATING_BLOCKING_HP_ERROR_CHECK',
    [DashboardAnomaliesNamesEnum.InletToOutsideTempSensorCheck]:
      '+REMOTE_SERVICE.INLET_TO_OUTSIDE_TEMP_SENSOR_CHECK',
    [DashboardAnomaliesNamesEnum.HeatingRodHeatingHighModulationErrorCheck]:
      '+REMOTE_SERVICE.HEATING_ROD_HEATING_HIGH_MODULATION_ERROR_CHECK',
    [DashboardAnomaliesNamesEnum.HeatingRodHeatingPauseErrorCheck]:
      '+REMOTE_SERVICE.HEATING_ROD_HEATING_PAUSE_ERROR_CHECK',
    [DashboardAnomaliesNamesEnum.RefrigerantPressureE3]: '+REMOTE_SERVICE.REFRIGERANT_PRESSURE_E3',
    [DashboardAnomaliesNamesEnum.SpfYear]: '+REMOTE_SERVICE.SPF_YEAR',
    [DashboardAnomaliesNamesEnum.SpfMonth]: '+REMOTE_SERVICE.SPF_MONTH',
    [DashboardAnomaliesNamesEnum.HeatCurveOptimization]: '+REMOTE_SERVICE.HEAT_CURVE_OPTIMIZATION',
    [DashboardAnomaliesNamesEnum.HeatingInSummer]: '+REMOTE_SERVICE.HEATING_IN_SUMMER',
    [DashboardAnomaliesNamesEnum.CompressorModulationActiveMinCompressorClocking]:
      '+REMOTE_SERVICE.COMPRESSOR_MODULATION_ACTIVE_MIN_COMPRESSOR_CLOCKING',
    [DashboardAnomaliesNamesEnum.CompressorModulationActiveMinHeatingRod]:
      '+REMOTE_SERVICE.COMPRESSOR_MODULATION_ACTIVE_MIN_HEATING_ROD',
    [DashboardAnomaliesNamesEnum.CompressorModulationActiveMinCompressorClockingLongTerm]:
      '+REMOTE_SERVICE.COMPRESSOR_MODULATION_ACTIVE_MIN_COMPRESSOR_CLOCKING_LONGTERM',
    [DashboardAnomaliesNamesEnum.EfficiencyGasBoiler]: '+REMOTE_SERVICE.EFFICIENCY_GAS_BOILER',
    [DashboardAnomaliesNamesEnum.EfficiencyVitovalorFuelcell]:
      '+REMOTE_SERVICE.EFFICIENCY_VITOVALOR_FUELCELL',
    [DashboardAnomaliesNamesEnum.OverheatCheckHeating]: '+REMOTE_SERVICE.OVERHEAT_CHECK_HEATING',
    [DashboardAnomaliesNamesEnum.OverheatCheckDhw]: '+REMOTE_SERVICE.OVERHEAT_CHECK_DHW',
    [DashboardAnomaliesNamesEnum.OverheatCheckCooling]: '+REMOTE_SERVICE.OVERHEAT_CHECK_COOLING',
    [DashboardAnomaliesNamesEnum.DhwTargetTemperature]: '+REMOTE_SERVICE.DHW_TARGET_TEMPERATURE',
    [DashboardAnomaliesNamesEnum.DhwDuration]: '+REMOTE_SERVICE.DHW_DURATION',
    [DashboardAnomaliesNamesEnum.CapacityCheckBattery0]: '+REMOTE_SERVICE.CAPACITY_CHECK_BATTERY_0',
    [DashboardAnomaliesNamesEnum.CapacityCheckBattery1]: '+REMOTE_SERVICE.CAPACITY_CHECK_BATTERY_1',
    [DashboardAnomaliesNamesEnum.CapacityCheckBattery2]: '+REMOTE_SERVICE.CAPACITY_CHECK_BATTERY_2',
    [DashboardAnomaliesNamesEnum.CapacityCheckBattery3]: '+REMOTE_SERVICE.CAPACITY_CHECK_BATTERY_3',
    [DashboardAnomaliesNamesEnum.CapacityCheckBattery4]: '+REMOTE_SERVICE.CAPACITY_CHECK_BATTERY_4',
    [DashboardAnomaliesNamesEnum.CapacityCheckBattery5]: '+REMOTE_SERVICE.CAPACITY_CHECK_BATTERY_5',
    [DashboardAnomaliesNamesEnum.CapacityCheckElectricityStorage]:
      '+REMOTE_SERVICE.CAPACITY_CHECK_ELECTRICITY_STORAGE',
    [DashboardAnomaliesNamesEnum.AgingCheckBattery0]: '+REMOTE_SERVICE.AGING_CHECK_BATTERY_0',
    [DashboardAnomaliesNamesEnum.AgingCheckBattery1]: '+REMOTE_SERVICE.AGING_CHECK_BATTERY_1',
    [DashboardAnomaliesNamesEnum.AgingCheckBattery2]: '+REMOTE_SERVICE.AGING_CHECK_BATTERY_2',
    [DashboardAnomaliesNamesEnum.AgingCheckBattery3]: '+REMOTE_SERVICE.AGING_CHECK_BATTERY_3',
    [DashboardAnomaliesNamesEnum.AgingCheckBattery4]: '+REMOTE_SERVICE.AGING_CHECK_BATTERY_4',
    [DashboardAnomaliesNamesEnum.AgingCheckBattery5]: '+REMOTE_SERVICE.AGING_CHECK_BATTERY_5',
    [DashboardAnomaliesNamesEnum.AgingThresholdCalculation]:
      '+REMOTE_SERVICE.AGING_THRESHOLD_CALCULATION',
    [DashboardAnomaliesNamesEnum.SetbackDurationOptimization]:
      '+REMOTE_SERVICE.SETBACK_DURATION_OPTIMIZATION',
    [DashboardAnomaliesNamesEnum.ReducedTemperatureOptimization]:
      '+REMOTE_SERVICE.REDUCED_TEMPERATURE_OPTIMIZATION',
    [DashboardAnomaliesNamesEnum.NormalTemperatureOptimization]:
      '+REMOTE_SERVICE.NORMAL_TEMPERATURE_OPTIMIZATION',
    [DashboardAnomaliesNamesEnum.RecommendationForDhwHysteresis]:
      '+REMOTE_SERVICE.RECOMMENDATION_FOR_DHW_HYSTERESIS',
    [DashboardAnomaliesNamesEnum.RefrigerantCircuitCheck1]:
      '+REMOTE_SERVICE.REFRIGERANT_CIRCUIT_CHECK1',
    [DashboardAnomaliesNamesEnum.RefrigerantCircuitCheck2]:
      '+REMOTE_SERVICE.REFRIGERANT_CIRCUIT_CHECK2',
    [DashboardAnomaliesNamesEnum.RefrigerantCircuitCheck3]:
      '+REMOTE_SERVICE.REFRIGERANT_CIRCUIT_CHECK3',
    [DashboardAnomaliesNamesEnum.RefrigerantCircuitCheck4]:
      '+REMOTE_SERVICE.REFRIGERANT_CIRCUIT_CHECK4',
    [DashboardAnomaliesNamesEnum.ShgProvidesHeat]: '+REMOTE_SERVICE.SHG_PROVIDES_HEAT',
    [DashboardAnomaliesNamesEnum.ShgClocking]: '+REMOTE_SERVICE.SHG_CLOCKING',
    [DashboardAnomaliesNamesEnum.HeatSourceAlternations]:
      '+REMOTE_SERVICE.HEAT_SOURCE_ALTERNATIONS',
    [DashboardAnomaliesNamesEnum.VitoairRuntime]: '+REMOTE_SERVICE.VITOAIR_RUNTIME',
    [DashboardAnomaliesNamesEnum.ConnectionQualityIndicator]:
      '+REMOTE_SERVICE.CONNECTION_QUALITY_INDICATOR',
    [DashboardAnomaliesNamesEnum.ConnectionQualityIndicatorCalculation]:
      '+REMOTE_SERVICE.CONNECTION_QUALITY_INDICATOR_CALCULATION',
    [DashboardAnomaliesNamesEnum.DeviceInformation]: '+REMOTE_SERVICE.DEVICE_INFORMATION',
    [DashboardAnomaliesNamesEnum.LeakageThresholdAnomalyUpper]:
      '+REMOTE_SERVICE.LEAKAGE_THRESHOLD_ANOMALY_UPPER',
    [DashboardAnomaliesNamesEnum.VitoairPipeValidation]: '+REMOTE_SERVICE.VITOAIR_PIPE_VALIDATION',
    [DashboardAnomaliesNamesEnum.ViairUniformTemperatureReading]:
      '+REMOTE_SERVICE.VIAIR_UNIFORM_TEMPERATURE_READING',
    [DashboardAnomaliesNamesEnum.VitoventMessageA0]: '+REMOTE_SERVICE.VITOVENT_MESSAGE_A0',
    [DashboardAnomaliesNamesEnum.VitoairValiditySensorSup]:
      '+REMOTE_SERVICE.VITOAIR_VALIDITY_SENSOR_SUP',
    [DashboardAnomaliesNamesEnum.VitoairValiditySensorEta]:
      '+REMOTE_SERVICE.VITOAIR_VALIDITY_SENSOR_ETA',
    [DashboardAnomaliesNamesEnum.VitoairValiditySensorEha]:
      '+REMOTE_SERVICE.VITOAIR_VALIDITY_SENSOR_EHA',
    [DashboardAnomaliesNamesEnum.VitoairValiditySensorOda]:
      '+REMOTE_SERVICE.VITOAIR_VALIDITY_SENSOR_ODA',
    [DashboardAnomaliesNamesEnum.VolFlowThresholdAnomalyDhwUpper]:
      '+REMOTE_SERVICE.VOL_FLOW_THRESHOLD_ANOMALY_DHW_UPPER',
    [DashboardAnomaliesNamesEnum.VolFlowThresholdAnomalyDefrostUpper]:
      '+REMOTE_SERVICE.VOL_FLOW_THRESHOLD_ANOMALY_DEFROST_UPPER',
    [DashboardAnomaliesNamesEnum.DhwVictimAnodeThresholdCheck]:
      '+REMOTE_SERVICE.DHW_VICTIM_ANODE_THRESHOLD_CHECK',
    [DashboardAnomaliesNamesEnum.ViairBypassValidationFunctions]:
      '+REMOTE_SERVICE.VIAIR_BYPASS_VALIDATION_FUNCTIONS',
    [DashboardAnomaliesNamesEnum.CapacityVsGuaranteed0]: '+REMOTE_SERVICE.CAPACITY_VS_GUARANTEED_0',
    [DashboardAnomaliesNamesEnum.CapacityVsGuaranteed1]: '+REMOTE_SERVICE.CAPACITY_VS_GUARANTEED_1',
    [DashboardAnomaliesNamesEnum.CapacityVsGuaranteed2]: '+REMOTE_SERVICE.CAPACITY_VS_GUARANTEED_2',
    [DashboardAnomaliesNamesEnum.CapacityVsGuaranteed3]: '+REMOTE_SERVICE.CAPACITY_VS_GUARANTEED_3',
    [DashboardAnomaliesNamesEnum.CapacityVsGuaranteed4]: '+REMOTE_SERVICE.CAPACITY_VS_GUARANTEED_4',
    [DashboardAnomaliesNamesEnum.CapacityVsGuaranteed5]: '+REMOTE_SERVICE.CAPACITY_VS_GUARANTEED_5',
    [DashboardAnomaliesNamesEnum.BatteryNominalCapacityCalculation]:
      '+REMOTE_SERVICE.BATTERY_NOMINAL_CAPACITY_CALCULATION',
    [DashboardAnomaliesNamesEnum.VolFlowThresholdAnomalyDhw]:
      '+REMOTE_SERVICE.VOL_FLOW_THRESHOLD_ANOMALY_DHW',
    [DashboardAnomaliesNamesEnum.VolFlowThresholdAnomalyDefrost]:
      '+REMOTE_SERVICE.VOL_FLOW_THRESHOLD_ANOMALY_DEFROST',
  };

  POWER_LIMITATION_MAPPING: Record<string, string> = {
    [`${PowerLimitationType.production}_${PowerLimitationMethod.hems}`]: '+INSTALLATIONS.INSTALLATION.POWER_LIMITATION.FEED_IN_LIMITATION_ENGERGY_MNG',
    [`${PowerLimitationType.production}_${PowerLimitationMethod.direct}`]: '+INSTALLATIONS.INSTALLATION.POWER_LIMITATION.FEED_IN_LIMITATION',
    [`${PowerLimitationType.consumption}_${PowerLimitationMethod.hems}`]: '+INSTALLATIONS.INSTALLATION.POWER_LIMITATION.CONSUMPTION_LIMIT_ENERGY_MNG',
    [`${PowerLimitationType.consumption}_${PowerLimitationMethod.direct}`]: '+INSTALLATIONS.INSTALLATION.POWER_LIMITATION.CONSUMPTION_LIMIT',
  };

  WALLBOX_CHARGING_MODE: Record<WallboxChargingState, string> = {
    [WallboxChargingState.activated]:
      '+INSTALLATIONS.INSTALLATION.WALLBOX.CHARGING_STATE_ACTIVATED',
    [WallboxChargingState.deactivated]:
      '+INSTALLATIONS.INSTALLATION.WALLBOX.CHARGING_STATE_DEACTIVATED',
  };
}

export type DhwOperatingMode =
  | 'comfort'
  | 'eco'
  | 'balanced'
  | 'off'
  | 'efficientWithMinComfort'
  | 'efficient';

export enum STATE {
  on = 'on',
  off = 'off',
  active = 'active',
  inactive = 'inactive',
}

export enum WIFI_STRENGTH {
  excellent = 'excellent',
  good = 'good',
  medium = 'medium',
  low = 'low',
  off = 'off',
}

export enum VentilationServiceLayerStatus {
  standby = 'standby',
  inPreparation = 'inPreparation',
  active = 'active',
  postProcessing = 'postProcessing',
}

export enum WallboxChargingState {
  activated = 'activated',
  deactivated = 'deactivated',
}

// TODO fix missing connected
export const ERROR_CONNECTION_STATUS: { [key: string]: string } = {
  // : Record<StatusValueDcFeature, string> = {
  [StatusValueDcFeature.notConnected]: '+INSTALLATIONS.INSTALLATION.FEATURE.VALUE.NOT_CONNECTED',
  [StatusValueDcFeature.error]: '+INSTALLATIONS.INSTALLATION.FEATURE.VALUE.ERROR',
};

export const DIVERTER_VALVE_VALUES: { [key: string]: string } = {
  [DiverterValve.dhw]: '+INSTALLATIONS.INSTALLATION.FEATURE.VALUE.DHW',
  [DiverterValve.heating]: '+INSTALLATIONS.INSTALLATION.FEATURE.VALUE.HEATING',
  [DiverterValve.middle]: '+INSTALLATIONS.INSTALLATION.FEATURE.VALUE.MIDDLE_POSITION',
  [DiverterValve.driving]: '+INSTALLATIONS.INSTALLATION.FEATURE.VALUE.DRIVING',
};

export const SCHEDULE_HINT_INFO: { [key: string]: string } = {
  [SCHEDULE_TYPE.DhwSchedule]: '+SCHEDULES.SCHEDULE_HINT_MSG.DHW',
  [SCHEDULE_TYPE.DhwCirculationSchedule]: '+SCHEDULES.SCHEDULE_HINT_MSG.DHW_CIRCULATION',
  [SCHEDULE_TYPE.HeatingSchedule]: '+SCHEDULES.SCHEDULE_HINT_MSG.HEATING',
  [SCHEDULE_TYPE.RoomSchedule]: '+SCHEDULES.SCHEDULE_HINT_MSG.ROOM',
  [SCHEDULE_TYPE.VentilationSchedule]: '+SCHEDULES.SCHEDULE_HINT_MSG.VENTILATION',
  [SCHEDULE_TYPE.NoiseReductionSchedule]: '+SCHEDULES.SCHEDULE_HINT_MSG.NOISE_REDUCTION',
  [SCHEDULE_TYPE.HybridPro]: '+SCHEDULES.SCHEDULE_HINT_MSG.NOISE_REDUCTION',
};

export enum GasConfigurationType {
  EGas = 'EGas',
  LiquidGas = 'LiquidGas',
  LLGas = 'LLGas',
}
